import ChatChannel from '../channels/chat_channel'
import ChatAPI from './chat_api'
import { formatMessageTime } from '../chat/utils'
import { buildUndoButton, clearUndoButtons, removeUndoneMessage, initChatUndo, onMessageUndone } from '../chat/undo'

initChatUndo()
onMessageUndone(() => refreshFilesCount())

const ROOT_SELECTOR = '[data-permit-chat-root="true"]'
const INPUT_ID = 'permit-chat-input'
const SEND_BTN_ID = 'permit-chat-send-btn'
const MESSAGES_ID = 'permit-chat-actual-messages'
const MESSAGES_CONTAINER_ID = 'permit-chat-messages-container'
const SENDING_INDICATOR_ID = 'permit-chat-sending-indicator'
const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024

const api = ChatAPI
const ROOT_LAYOUT_SELECTOR = '.permit-chat-layout'
// Gap left below the chat card so it doesn't touch the viewport edge.
const LAYOUT_BOTTOM_GAP = 24
let activeSubscription = null
let activeConversationId = null
let currentUserId = null
let resizeListenerBound = false

// @-mention autocomplete state. `start` is the index of the triggering '@' in the input.
const mentionState = { open: false, start: -1, options: [], activeIndex: 0 }
// Mentions chosen for the message currently being composed ({ id, name }).
let selectedMentions = []

function setup() {
  const root = document.querySelector(ROOT_SELECTOR)
  if (!root) return

  const conversationId = parseInt(root.getAttribute('data-conversation-id'), 10)
  if (!conversationId) return

  // setup() is registered on both DOMContentLoaded and turbolinks:load, which both fire on
  // an initial load. Without this guard the listeners below (notably the send button) get
  // bound twice, so one click posts the message twice. A real Turbolinks navigation replaces
  // the body, so the fresh root lacks the flag and re-binds correctly.
  if (root.dataset.permitChatBound === 'true') return
  root.dataset.permitChatBound = 'true'

  currentUserId = parseInt(document.body.getAttribute('data-user-id') || '0', 10) || null

  if (activeSubscription && String(activeConversationId) !== String(conversationId)) {
    ChatChannel.unsubscribe(activeConversationId)
  }
  activeSubscription = null
  activeConversationId = conversationId

  activeSubscription = ChatChannel.subscribe(conversationId, {
    onMessageUndone: ({ message_id: messageId }) => {
      removeUndoneMessage(messageId)
      refreshFilesCount()
    },
    onNewMessage: ({ message }) => {
      appendMessage(message)
      markAllRead()
    }
  })

  bindInput()
  bindSearch()
  bindPanelToggles()
  bindMembersToggle()
  resizeChatLayout()
  scrollMessagesToBottom()
  markAllRead()

  // The CSS height is a viewport-relative fallback; resize to the real space so
  // the card fills the screen instead of leaving a gap on tall monitors. Bound
  // once on window (which survives Turbolinks navigations) to avoid stacking
  // listeners; the handler no-ops when the chat isn't on the page.
  if (!resizeListenerBound) {
    window.addEventListener('resize', resizeChatLayout)
    resizeListenerBound = true
  }
}

// Size the chat card to fill from its top edge to near the viewport bottom.
// Using the measured offset (rather than a magic `100vh - Npx`) keeps it correct
// across the dashboard and contractor-portal headers, which differ in height.
function resizeChatLayout() {
  const layout = document.querySelector(ROOT_LAYOUT_SELECTOR)
  if (!layout) return

  const top = layout.getBoundingClientRect().top
  const minHeight = parseInt(window.getComputedStyle(layout).minHeight, 10) || 0
  const available = window.innerHeight - top - LAYOUT_BOTTOM_GAP
  layout.style.height = `${Math.max(available, minHeight)}px`
}

function markAllRead() {
  if (!activeConversationId) return
  api.markAllRead(activeConversationId).catch(() => {})
}

function bindInput() {
  const input = document.getElementById(INPUT_ID)
  const sendBtn = document.getElementById(SEND_BTN_ID)
  if (!input || !sendBtn) return

  autoGrow(input)

  input.addEventListener('input', () => {
    autoGrow(input)
    handleMentionInput(input)
  })
  input.addEventListener('keydown', (event) => {
    // When the mention dropdown is open, it owns the arrow/enter/escape keys.
    if (mentionState.open && handleMentionKeydown(event, input)) return

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      sendCurrentMessage()
    }
  })
  input.addEventListener('blur', () => {
    // Delay so a click on a dropdown option (mousedown) can fire first.
    setTimeout(hideMentionDropdown, 150)
  })
  input.addEventListener('paste', handlePasteImage)
  sendBtn.addEventListener('click', () => sendCurrentMessage())

  document.querySelectorAll('[data-action="permit-chat-show-file-upload"]').forEach((btn) => {
    btn.addEventListener('click', () => openFilePicker())
  })
}

// ---- @-mention autocomplete ---------------------------------------------------

// Participants are read from the Team Members panel (data-user-id / data-user-name),
// so the dropdown always reflects the current roster without a separate fetch.
function mentionParticipants() {
  const people = Array.from(document.querySelectorAll('.participant-item[data-user-id]'))
    .map((el) => ({ id: el.getAttribute('data-user-id'), name: el.getAttribute('data-user-name') || '' }))
    .filter((p) => p.id && p.name)
  // Synthetic "All" broadcast: serializes to @[All](all); the server expands it
  // to every active participant so everyone hears the mention sound.
  return [{ id: 'all', name: 'All' }, ...people]
}

// Detect an "@query" token immediately before the caret and open/refresh the dropdown.
function handleMentionInput(input) {
  const caret = input.selectionStart
  const before = input.value.slice(0, caret)
  const match = before.match(/@([^@\n]*)$/)
  if (!match) return hideMentionDropdown()

  const query = match[1].toLowerCase()
  const options = mentionParticipants()
    .filter((p) => p.name.toLowerCase().includes(query))
    .slice(0, 8)
  if (options.length === 0) return hideMentionDropdown()

  mentionState.open = true
  mentionState.start = caret - match[0].length // index of the '@'
  mentionState.options = options
  mentionState.activeIndex = 0
  renderMentionDropdown(input)
}

function handleMentionKeydown(event, input) {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      mentionState.activeIndex = (mentionState.activeIndex + 1) % mentionState.options.length
      renderMentionDropdown(input)
      return true
    case 'ArrowUp':
      event.preventDefault()
      mentionState.activeIndex = (mentionState.activeIndex - 1 + mentionState.options.length) % mentionState.options.length
      renderMentionDropdown(input)
      return true
    case 'Enter':
    case 'Tab':
      event.preventDefault()
      insertMention(input, mentionState.options[mentionState.activeIndex])
      return true
    case 'Escape':
      event.preventDefault()
      hideMentionDropdown()
      return true
    default:
      return false
  }
}

// Insert the human-readable "@Name " into the textarea (a textarea can't render
// styled tokens), and remember the name→id mapping so we can rewrite it to the
// @[Name](id) markup the server expects at send time (see serializeMentions).
function insertMention(input, participant) {
  if (!participant) return
  const caret = input.selectionStart
  const token = `@${participant.name} `
  input.value = input.value.slice(0, mentionState.start) + token + input.value.slice(caret)
  const newCaret = mentionState.start + token.length
  input.setSelectionRange(newCaret, newCaret)
  if (!selectedMentions.some((m) => m.id === participant.id && m.name === participant.name)) {
    selectedMentions.push({ id: participant.id, name: participant.name })
  }
  hideMentionDropdown()
  autoGrow(input)
  input.focus()
}

// Rewrite display mentions ("@Name") back to "@[Name](id)" before sending.
// Longer names first so a name that is a prefix of another isn't half-matched.
function serializeMentions(content) {
  let out = content
  selectedMentions
    .slice()
    .sort((a, b) => b.name.length - a.name.length)
    .forEach((m) => {
      out = out.split(`@${m.name}`).join(`@[${m.name}](${m.id})`)
    })
  return out
}

function renderMentionDropdown(input) {
  const wrapper = input.closest('.conversation-input-wrapper') || input.parentElement
  if (!wrapper) return
  wrapper.style.position = wrapper.style.position || 'relative'

  let dropdown = document.getElementById('permit-chat-mention-dropdown')
  if (!dropdown) {
    dropdown = document.createElement('div')
    dropdown.id = 'permit-chat-mention-dropdown'
    dropdown.className = 'mention-dropdown'
    wrapper.appendChild(dropdown)
  }

  dropdown.innerHTML = ''
  mentionState.options.forEach((p, i) => {
    const option = document.createElement('div')
    option.className = `mention-option${i === mentionState.activeIndex ? ' active' : ''}`
    option.textContent = p.name
    // mousedown (not click) so it fires before the input's blur handler.
    option.addEventListener('mousedown', (e) => {
      e.preventDefault()
      insertMention(input, p)
    })
    dropdown.appendChild(option)
  })
  dropdown.classList.add('visible')
}

function hideMentionDropdown() {
  mentionState.open = false
  mentionState.options = []
  const dropdown = document.getElementById('permit-chat-mention-dropdown')
  if (dropdown) dropdown.classList.remove('visible')
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ))
}

// JS mirror of ChatHelper#render_chat_mentions: turn @[Name](id) into highlighted
// spans while escaping everything else, so client-appended messages match the
// server-rendered ones.
function renderMentionsHtml(text) {
  const pattern = /@\[([^\]]+)\]\((\d+|all)\)/g
  let result = ''
  let last = 0
  let match
  while ((match = pattern.exec(text)) !== null) {
    result += escapeHtml(text.slice(last, match.index))
    result += `<span class="chat-mention" data-user-id="${escapeHtml(match[2])}">@${escapeHtml(match[1])}</span>`
    last = pattern.lastIndex
  }
  result += escapeHtml(text.slice(last))
  return result
}

function autoGrow(textarea) {
  textarea.style.height = 'auto'
  textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
}

async function sendCurrentMessage(attachments = []) {
  if (!activeConversationId) return
  const input = document.getElementById(INPUT_ID)
  const content = input ? serializeMentions(input.value.trim()) : ''
  if (!content && attachments.length === 0) return

  toggleSending(true)
  try {
    const result = await api.sendMessage(activeConversationId, content, attachments)
    if (input) {
      input.value = ''
      autoGrow(input)
    }
    selectedMentions = []
    if (result && result.message) {
      appendMessage(result.message)
    }
  } catch (error) {
    alert(error?.message || 'Failed to send message')
  } finally {
    toggleSending(false)
  }
}

// Paste screenshots/images straight from the clipboard into the chat (e.g. a
// Cmd/Ctrl+V of a screenshot) — uploaded as image attachments, no save-and-pick.
async function handlePasteImage(event) {
  const items = (event.clipboardData && event.clipboardData.items) || []
  const imageFiles = []
  for (const item of items) {
    if (item.kind === 'file' && item.type && item.type.indexOf('image/') === 0) {
      const file = item.getAsFile()
      if (file) imageFiles.push(file)
    }
  }
  // No image in the clipboard — let the normal text paste happen.
  if (imageFiles.length === 0) return
  event.preventDefault()

  const attachments = []
  for (const raw of imageFiles) {
    if (raw.size > MAX_FILE_SIZE_BYTES) {
      alert('Pasted image exceeds the 200MB limit.')
      return
    }
    const file = namedPastedImage(raw)
    attachments.push({
      file,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      attachment_type: api.determineAttachmentType(file.type)
    })
  }
  await sendCurrentMessage(attachments)
}

// Clipboard images arrive unnamed (or as "image.png"); give them a unique name.
function namedPastedImage(file) {
  const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg')
  const name = `screenshot-${Date.now()}.${ext}`
  try {
    return new File([file], name, { type: file.type })
  } catch (e) {
    return file
  }
}

function openFilePicker() {
  const picker = document.createElement('input')
  picker.type = 'file'
  picker.multiple = true
  picker.accept = 'image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  picker.style.display = 'none'

  picker.addEventListener('change', async () => {
    const files = Array.from(picker.files || [])
    picker.remove()
    if (files.length === 0) return

    const attachments = []
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert(`${file.name} exceeds the 200MB limit.`)
        return
      }
      attachments.push({
        file,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        attachment_type: api.determineAttachmentType(file.type)
      })
    }
    await sendCurrentMessage(attachments)
  })

  document.body.appendChild(picker)
  picker.click()
}

function toggleSending(isSending) {
  const indicator = document.getElementById(SENDING_INDICATOR_ID)
  const sendBtn = document.getElementById(SEND_BTN_ID)
  if (indicator) indicator.classList.toggle('hidden', !isSending)
  if (sendBtn) sendBtn.disabled = isSending
}

function appendMessage(message) {
  const container = document.getElementById(MESSAGES_ID)
  if (!container) return
  if (message.id && container.querySelector(`[data-message-id="${message.id}"]`)) return

  const empty = container.querySelector('.empty-messages-state')
  if (empty) empty.remove()

  const isUserMessage = Number(message.user_id) === Number(currentUserId)

  if (message.message_type === 'system' || message.message_type === 4) {
    const wrapper = document.createElement('div')
    wrapper.className = 'message-wrapper system-message'
    wrapper.setAttribute('data-message-id', message.id)
    const notice = document.createElement('div')
    notice.className = 'system-message-notice'
    notice.textContent = message.content || ''
    wrapper.appendChild(notice)
    container.appendChild(wrapper)
    scrollMessagesToBottom()
    return
  }

  if (!isUserMessage) clearUndoButtons(container)

  const wrapper = document.createElement('div')
  wrapper.className = `message-wrapper ${isUserMessage ? 'user-message' : 'other-message'}`
  wrapper.setAttribute('data-message-id', message.id)

  if (!isUserMessage) {
    const avatar = document.createElement('div')
    avatar.className = 'message-avatar'
    const initialsSpan = document.createElement('span')
    initialsSpan.className = 'avatar-initials'
    const first = message.user?.first_name?.[0] || ''
    const last = message.user?.last_name?.[0] || ''
    // chat_display_initials is 'DU' for deactivated accounts
    initialsSpan.textContent = message.user?.chat_display_initials || (first + last).trim() || message.user?.email?.[0]?.toUpperCase() || 'U'
    avatar.appendChild(initialsSpan)
    wrapper.appendChild(avatar)
  }

  const content = document.createElement('div')
  content.className = 'message-content'

  const header = document.createElement('div')
  header.className = 'message-header'
  const sender = document.createElement('span')
  sender.className = 'message-sender message-sender--top'
  sender.textContent = isUserMessage ? 'You' : (message.user?.chat_display_name || message.user?.full_name || message.user?.email || 'Unknown')
  const time = document.createElement('span')
  time.className = 'message-time message-time--top'
  time.textContent = formatMessageTime(message.created_at)
  time.setAttribute('data-tooltip', formatFullDateTime(message.created_at))
  header.appendChild(sender)
  if (!isUserMessage && message.user?.role) {
    const roleSpan = document.createElement('span')
    roleSpan.className = 'message-sender-role'
    roleSpan.textContent = humanizeRole(message.user.role)
    header.appendChild(roleSpan)
  }
  header.appendChild(time)
  const undoButton = buildUndoButton(message, currentUserId)
  if (undoButton) header.appendChild(undoButton)
  content.appendChild(header)

  const bubble = document.createElement('div')
  bubble.className = `message-bubble ${isUserMessage ? 'user-bubble' : 'other-bubble'}`

  if (Array.isArray(message.chat_message_attachments)) {
    message.chat_message_attachments.forEach((attachment) => {
      bubble.appendChild(renderAttachment(attachment))
    })
  }

  if (message.content) {
    const text = document.createElement('p')
    text.className = 'message-text'
    text.innerHTML = renderMentionsHtml(message.content)
    bubble.appendChild(text)
  }

  content.appendChild(bubble)
  wrapper.appendChild(content)
  container.appendChild(wrapper)
  addAttachmentsToFilesPanel(message)
  scrollMessagesToBottom()
}

// Keep the Files side panel in sync when a message with attachments arrives.
// The panel is rendered server-side on load; without this, files sent during
// the session never show up until a full page reload.
function addAttachmentsToFilesPanel(message) {
  const attachments = message.chat_message_attachments
  if (!Array.isArray(attachments) || attachments.length === 0) return

  const list = document.querySelector('.files-list')
  if (!list) return
  const placeholder = list.querySelector('.no-files')
  if (placeholder) placeholder.remove()

  const isUserMessage = Number(message.user_id) === Number(currentUserId)
  const uploader = isUserMessage ? 'You' : (message.user?.chat_display_name || message.user?.full_name || message.user?.email || 'Unknown')
  const date = formatMessageTime(message.created_at)

  // Newest first, matching the server ordering (created_at DESC).
  attachments.forEach((att) => {
    const url = att.url || att.file_url || ''
    list.prepend(buildFileItem({
      name: att.file_name || 'File',
      size: formatFileSize(att.file_size),
      uploader,
      date,
      url
    }))
  })

  refreshFilesCount()
}

function buildFileItem({ name, size, uploader, date, url }) {
  const item = document.createElement('div')
  item.className = 'file-item'
  item.innerHTML = `
    <div class="file-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
      </svg>
    </div>
    <div class="file-info">
      <div class="file-name"></div>
      ${size ? '<div class="file-size"></div>' : ''}
      <div class="file-meta">
        <span class="file-uploader"></span>
        <span class="file-separator">•</span>
        <span class="file-date"></span>
      </div>
    </div>`
  item.querySelector('.file-name').textContent = name
  if (size) item.querySelector('.file-size').textContent = size
  item.querySelector('.file-uploader').textContent = uploader
  item.querySelector('.file-date').textContent = date

  if (url) {
    const link = document.createElement('a')
    link.className = 'file-download'
    link.href = url
    link.target = '_blank'
    link.setAttribute('download', name)
    link.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
      </svg>`
    item.appendChild(link)
  }
  return item
}

function formatFileSize(bytes) {
  if (!bytes) return ''
  return `${(Number(bytes) / 1024).toFixed(1)} KB`
}

// Recompute "Files (N)" from the actual rendered items so the count never drifts.
function refreshFilesCount() {
  const list = document.querySelector('.files-list')
  if (!list) return
  const title = list.closest('.permit-chat-side-section')?.querySelector('.section-title')
  if (title) title.textContent = `Files (${list.querySelectorAll('.file-item').length})`
}

function renderAttachment(attachment) {
  const type = attachment.attachment_type
  const url = attachment.url || attachment.file_url || '#'

  if (type === 'image' || type === 0) {
    const img = document.createElement('img')
    img.className = 'message-image'
    img.src = url
    img.alt = attachment.file_name || 'Image'
    return img
  }
  if (type === 'video' || type === 1) {
    const video = document.createElement('video')
    video.className = 'message-video'
    video.controls = true
    video.src = url
    return video
  }
  const link = document.createElement('a')
  link.className = 'message-file-attachment'
  link.href = url
  link.target = '_blank'
  link.textContent = attachment.file_name || 'File'
  return link
}

function scrollMessagesToBottom() {
  // Scroll the overflow container (the section), not the inner flex list.
  const container = document.getElementById(MESSAGES_CONTAINER_ID)
  if (!container) return
  container.scrollTop = container.scrollHeight
  // Run again next frame so we still land at the bottom after images/late layout
  // change the scroll height (notably on initial load and refresh).
  requestAnimationFrame(() => { container.scrollTop = container.scrollHeight })
}

// Full date + time for the message-time hover tooltip (mirrors the server's time_full).
function formatFullDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  })
}

function humanizeRole(role) {
  return String(role).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Collapsible Team Members / Files side panels (chevron in each section header).
function bindPanelToggles() {
  document.querySelectorAll('[data-action="permit-chat-toggle-panel"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const section = btn.closest('.permit-chat-side-section')
      if (section) section.classList.toggle('collapsed')
    })
  })
}

// Show/hide the whole Team Members + Files side panel by clicking the chat header
// ("Permit Team Chat / N Members") — mirrors the master chat, where clicking the
// header name toggles the info panel. Collapsing widens the message area.
function bindMembersToggle() {
  const layout = document.querySelector(ROOT_LAYOUT_SELECTOR)
  const trigger = document.querySelector('[data-action="permit-chat-toggle-members"]')
  if (!layout || !trigger) return

  const toggle = () => {
    layout.classList.toggle('side-collapsed')
    resizeChatLayout()
  }

  trigger.addEventListener('click', toggle)
  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggle()
    }
  })
}

// In-conversation message search: filters + highlights the loaded messages.
function bindSearch() {
  const toggle = document.querySelector('[data-action="permit-chat-toggle-search"]')
  const bar = document.getElementById('permit-chat-search-bar')
  const input = document.getElementById('permit-chat-search-input')
  const clearBtn = document.querySelector('[data-action="permit-chat-clear-search"]')
  const closeBtn = document.querySelector('[data-action="permit-chat-close-search"]')
  if (!toggle || !bar || !input) return

  // The "Clear" button only makes sense when there's a query to clear.
  const updateClearVisibility = () => {
    if (clearBtn) clearBtn.classList.toggle('hidden', input.value.trim() === '')
  }

  // Empty the query but keep the bar open (the "Clear" button).
  const clearSearch = () => {
    input.value = ''
    filterMessages('')
    updateClearVisibility()
    input.focus()
  }

  // Dismiss the bar entirely (the ✕ button, or toggling while it's open). Reset
  // so it reopens fresh and the message list is no longer filtered/highlighted.
  const closeSearch = () => {
    bar.classList.add('hidden')
    input.value = ''
    filterMessages('')
    updateClearVisibility()
  }

  toggle.addEventListener('click', () => {
    if (bar.classList.contains('hidden')) {
      bar.classList.remove('hidden')
      input.focus()
    } else {
      closeSearch()
    }
  })

  input.addEventListener('input', () => {
    filterMessages(input.value)
    updateClearVisibility()
  })

  if (clearBtn) clearBtn.addEventListener('click', clearSearch)
  if (closeBtn) closeBtn.addEventListener('click', closeSearch)
}

function filterMessages(rawQuery) {
  const container = document.getElementById(MESSAGES_ID)
  const countEl = document.getElementById('permit-chat-search-count')
  if (!container) return

  const query = (rawQuery || '').trim().toLowerCase()
  let matches = 0

  container.querySelectorAll('.message-wrapper').forEach((wrapper) => {
    const textEl = wrapper.querySelector('.message-text')
    clearHighlight(textEl)

    if (!query) {
      wrapper.classList.remove('search-hidden')
      return
    }

    const text = (textEl?.textContent || '').toLowerCase()
    const sender = (wrapper.querySelector('.message-sender')?.textContent || '').toLowerCase()
    const isMatch = text.includes(query) || sender.includes(query)
    wrapper.classList.toggle('search-hidden', !isMatch)
    if (isMatch) {
      matches += 1
      highlight(textEl, query)
    }
  })

  if (countEl) countEl.textContent = query ? `${matches} result${matches === 1 ? '' : 's'}` : ''
}

// Highlight only plain-text messages so rendered mention markup is never corrupted.
function highlight(textEl, query) {
  if (!textEl || !query || textEl.children.length > 0) return
  const original = textEl.textContent
  const idx = original.toLowerCase().indexOf(query)
  if (idx === -1) return

  const mark = document.createElement('mark')
  mark.textContent = original.slice(idx, idx + query.length)
  textEl.textContent = ''
  textEl.append(
    document.createTextNode(original.slice(0, idx)),
    mark,
    document.createTextNode(original.slice(idx + query.length))
  )
}

function clearHighlight(textEl) {
  if (textEl && textEl.querySelector('mark')) textEl.textContent = textEl.textContent
}

document.addEventListener('DOMContentLoaded', setup)
document.addEventListener('turbolinks:load', setup)
